import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import { useAuthStore } from '../../lib/auth';

interface Home { id: string; nickname: string | null; address_line1: string; }
interface Doc {
  id: string;
  type: string;
  name: string;
  file_size: number | null;
  expiry_date: string | null;
  created_at: string;
  download_url: string;
}

const CHIPS = ['all', 'warranty', 'receipt', 'manual', 'inspection', 'other'] as const;
type Chip = typeof CHIPS[number];

const CHIP_LABELS: Record<Chip, string> = {
  all: 'All', warranty: 'Warranties', receipt: 'Receipts',
  manual: 'Manuals', inspection: 'Inspection', other: 'Other',
};
const TYPE_ICONS: Record<string, string> = {
  all: '📄', warranty: '📋', receipt: '🧾',
  manual: '📖', inspection: '🔍', other: '📎',
};

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;

function fmtBytes(n: number | null): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function DocumentVault() {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  const [chip, setChip] = useState<Chip>('all');
  const [docType, setDocType] = useState<Chip>('other');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: homesRes } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homeId = homesRes?.data?.[0]?.id;

  const { data: docsRes, isLoading } = useQuery({
    queryKey: ['documents', homeId, chip],
    queryFn: () =>
      api.get<{ data: Doc[] }>(`/homes/${homeId}/documents${chip !== 'all' ? `?type=${chip}` : ''}`),
    enabled: !!homeId,
  });

  const docs = docsRes?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: async (docId: string) => {
      const res = await fetch(`/api/v1/homes/${homeId}/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken ?? ''}` },
      });
      if (!res.ok) throw new Error('Delete failed');
    },
    onSuccess: () => {
      setDeletingId(null);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !homeId) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      setUploadErr('Only PDF, JPG, PNG, and WEBP files are accepted.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadErr('File must be 10 MB or smaller.');
      return;
    }

    setUploadErr(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', docType === 'all' ? 'other' : docType);

      const res = await fetch(`/api/v1/homes/${homeId}/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken ?? ''}` },
        body: fd,
      });

      if (!res.ok) {
        const body = await res.json() as { error?: { message?: string } };
        setUploadErr(body.error?.message ?? 'Upload failed.');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch {
      setUploadErr('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDownload = async (doc: Doc) => {
    try {
      const res = await fetch(`/api/v1${doc.download_url}`, {
        headers: { Authorization: `Bearer ${accessToken ?? ''}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const handleExport = async () => {
    if (!homeId) return;
    try {
      const res = await api.get<{ data: Doc[] }>(`/homes/${homeId}/documents`);
      const all = res.data;
      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
        <title>Home Guard — Document Vault</title>
        <style>
          body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:32px;max-width:800px;margin:0 auto;color:#111827}
          h1{color:#1D4ED8;font-size:22px;font-weight:700;border-bottom:2px solid #e5e7eb;padding-bottom:12px;margin-bottom:8px}
          .sub{color:#6b7280;font-size:13px;margin:0 0 24px}
          table{width:100%;border-collapse:collapse}
          th{background:#f9fafb;text-align:left;padding:8px 12px;font-size:11px;text-transform:uppercase;color:#6b7280;border-bottom:1px solid #e5e7eb;letter-spacing:.05em}
          td{padding:10px 12px;border-bottom:1px solid #f3f4f6;font-size:13px;vertical-align:top}
          .badge{display:inline-block;padding:2px 8px;border-radius:99px;background:#eff6ff;color:#1D4ED8;font-size:11px;font-weight:600;text-transform:capitalize}
          @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
        </style>
      </head><body>
        <h1>Document Vault</h1>
        <p class="sub">Exported ${new Date().toLocaleDateString()} &middot; ${all.length} document${all.length !== 1 ? 's' : ''}</p>
        <table>
          <thead><tr><th>Name</th><th>Type</th><th>Size</th><th>Uploaded</th><th>Expiry</th></tr></thead>
          <tbody>${all.map(d => `
            <tr>
              <td>${d.name}</td>
              <td><span class="badge">${d.type}</span></td>
              <td>${fmtBytes(d.file_size)}</td>
              <td>${fmtDate(d.created_at)}</td>
              <td>${d.expiry_date ? fmtDate(d.expiry_date) : '—'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); w.print(); }
    } catch { /* silent */ }
  };

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 96 }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e40af, #1D4ED8)', paddingTop: 48, paddingBottom: 20, paddingLeft: 20, paddingRight: 20 }}>
        <h1 style={{ color: '#fff', fontWeight: 700, fontSize: 20, margin: 0 }}>Document Vault</h1>
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, margin: '4px 0 0' }}>Warranties, receipts &amp; manuals</p>
      </div>

      {/* Filter chips */}
      <div style={{ background: '#fff', borderBottom: '1px solid #f3f4f6', padding: '12px 16px', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
          {CHIPS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setChip(c)}
              aria-pressed={chip === c}
              style={{
                padding: '6px 14px', borderRadius: 99, fontSize: 13, fontWeight: 500,
                border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 36,
                background: chip === c ? '#1D4ED8' : '#f3f4f6',
                color: chip === c ? '#fff' : '#374151',
              }}
            >
              {CHIP_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px 16px 0' }}>
        {/* Upload panel */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 16 }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: '#111827' }}>Upload document</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={docType === 'all' ? 'other' : docType}
              onChange={e => setDocType(e.target.value as Chip)}
              aria-label="Document type"
              style={{
                padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                fontSize: 13, background: '#fff', color: '#374151', cursor: 'pointer', minHeight: 40,
              }}
            >
              {CHIPS.filter(c => c !== 'all').map(c => (
                <option key={c} value={c}>{CHIP_LABELS[c]}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !homeId}
              aria-label="Choose file to upload"
              style={{
                flex: 1, padding: '8px 16px', borderRadius: 10, border: 'none',
                fontSize: 13, fontWeight: 600, minHeight: 40,
                background: uploading ? '#93c5fd' : '#1D4ED8',
                color: '#fff', cursor: uploading || !homeId ? 'not-allowed' : 'pointer',
              }}
            >
              {uploading ? 'Uploading…' : 'Choose file'}
            </button>
          </div>
          {uploadErr && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>{uploadErr}</p>
          )}
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9ca3af' }}>PDF, JPG, PNG or WEBP · max 10 MB</p>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            style={{ display: 'none' }}
            aria-hidden="true"
            onChange={e => void handleFileSelect(e)}
          />
        </div>

        {/* Document list */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div style={{
              width: 28, height: 28, border: '3px solid #1D4ED8',
              borderTopColor: 'transparent', borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : !homeId ? (
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <p style={{ margin: 0, fontSize: 14, color: '#9ca3af' }}>Add your home first to store documents.</p>
          </div>
        ) : docs.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{TYPE_ICONS[chip] ?? '📄'}</div>
            <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: '#111827' }}>
              {chip === 'all' ? 'No documents yet' : `No ${CHIP_LABELS[chip].toLowerCase()} yet`}
            </p>
            <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>Upload your first document above.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map(doc => (
              <div
                key={doc.id}
                style={{
                  background: '#fff', borderRadius: 14, padding: '12px 14px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
              >
                <div style={{ fontSize: 26, flexShrink: 0 }}>{TYPE_ICONS[doc.type] ?? '📄'}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: 14, fontWeight: 600, color: '#111827',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {doc.name}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                    <span style={{ textTransform: 'capitalize' }}>{doc.type}</span>
                    {doc.file_size ? ` · ${fmtBytes(doc.file_size)}` : ''}
                    {` · ${fmtDate(doc.created_at)}`}
                    {doc.expiry_date ? ` · Expires ${fmtDate(doc.expiry_date)}` : ''}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => void handleDownload(doc)}
                    aria-label={`Download ${doc.name}`}
                    style={{
                      padding: '6px 10px', borderRadius: 8,
                      border: '1.5px solid #e5e7eb', background: '#fff',
                      fontSize: 14, cursor: 'pointer', color: '#374151', lineHeight: 1,
                    }}
                  >
                    ↓
                  </button>

                  {deletingId === doc.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setDeletingId(null)}
                        style={{
                          padding: '6px 8px', borderRadius: 8,
                          border: '1.5px solid #e5e7eb', background: '#fff',
                          fontSize: 11, cursor: 'pointer', color: '#6b7280',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(doc.id)}
                        disabled={deleteMutation.isPending}
                        style={{
                          padding: '6px 10px', borderRadius: 8, border: 'none',
                          background: '#dc2626', fontSize: 11, fontWeight: 600,
                          cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                          color: '#fff',
                        }}
                      >
                        {deleteMutation.isPending ? '…' : 'Delete'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeletingId(doc.id)}
                      aria-label={`Delete ${doc.name}`}
                      style={{
                        padding: '6px 10px', borderRadius: 8,
                        border: '1.5px solid #fecaca', background: '#fef2f2',
                        fontSize: 12, cursor: 'pointer', color: '#dc2626', lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Export / print */}
      {docs.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 80, left: 0, right: 0,
          padding: '0 16px', display: 'flex', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <button
            type="button"
            onClick={() => void handleExport()}
            style={{
              pointerEvents: 'auto', padding: '10px 20px',
              background: '#fff', border: '1.5px solid #e5e7eb',
              borderRadius: 12, fontSize: 13, fontWeight: 500, color: '#374151',
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export &amp; print
          </button>
        </div>
      )}

      <NavBar />
    </div>
  );
}
